import React, { useState, useEffect } from 'react';
import { Button, Container, Text, DropdownInput } from 'components';
import { dataTrackingOperations, hooks, mainOperations, mainSelectors } from 'state';
import { eventsCategory, navigationEvents } from 'utils/constants/events';
import { FiMail, FiUsers, FiCheckCircle, FiClock, FiXCircle } from 'react-icons/fi';
import { getUserFromSubcookies } from 'utils/tools';

import InviteForm from './InviteForm';
import InviteList from './InviteList';
import type { InviteData } from './types';
import ConfirmDialog, { SuccessMessage } from '../ConfirmDialog';
import './pos-invite-manager.css';

export type { InviteData } from './types';

interface POSInviteManagerProps {
  isOpen: boolean;
  onClose: () => void;
  userRole: 'brand' | 'customer_success';
  brandName?: string;
}

const POSInviteManager: React.FC<POSInviteManagerProps> = ({
  isOpen,
  onClose,
  userRole,
  brandName
}) => {
  const [activeTab, setActiveTab] = useState<'invite' | 'tracking'>('invite');
  const [invites, setInvites] = useState<InviteData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingInviteData, setPendingInviteData] = useState<Omit<InviteData, 'id' | 'invitedAt' | 'status' | 'inviteLink'> | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [retailers, setRetailers] = useState<any[]>([]);
  const [apiSynced, setApiSynced] = useState<any[]>([]);
  const [invitationTracking, setInvitationTracking] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [selectedStateCode, setSelectedStateCode] = useState<string>('');

  const { useAppDispatch, useAppSelector } = hooks;
  const { trackEvent } = dataTrackingOperations;
  const { getRetailers, getApiSynced, getInvitationTracking, sendEmailToken, getStates } = mainOperations;
  const { selectmain } = mainSelectors;
  const dispatch = useAppDispatch();
  const { selectedWorkbookV2 } = useAppSelector(selectmain);

  const stateCodeControl = selectedWorkbookV2?.sigmaControls?.find(
    (control: any) => control.sigmaControlId === 'ctrl_STATE_CODE'
  );
  const defaultStateCode = stateCodeControl?.values?.split(',')[0]?.trim() || '';

  useEffect(() => {
    if (isOpen) {
      setActiveTab('invite');
      handleLoadStates();
      if (defaultStateCode) {
        setSelectedStateCode(defaultStateCode);
      }
    } else {
      setActiveTab('invite');
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedStateCode && isOpen) {
      handleLoadRetailers();
    }
  }, [selectedStateCode, isOpen]);

  useEffect(() => {
    if (isOpen) {
      handleLoadApiSynced();
      handleLoadInvitationTracking();
    }
  }, [isOpen]);


  const handleSendInvite = async (inviteData: Omit<InviteData, 'id' | 'invitedAt' | 'status' | 'inviteLink'>) => {
    setPendingInviteData(inviteData);
    setShowConfirmDialog(true);
  };

  const handleConfirmInvite = async () => {
    if (!pendingInviteData) return;
    
    setIsLoading(true);
    setShowConfirmDialog(false);
    
    try {
      const userGuid = getUserFromSubcookies().profile?.sub || "";

      const emailData = {
        userGuid: userGuid,
        emailAddress: pendingInviteData.retailerEmail,
        retailerId: pendingInviteData.retailerId || ''
      };

      const result = await dispatch(sendEmailToken(emailData));
      
      if (sendEmailToken.fulfilled.match(result)) {
        
        const newInvite: InviteData = {
          ...pendingInviteData,
          id: Date.now().toString(),
          invitedAt: new Date(),
          status: 'sent',
          inviteLink: `https://pos-connect.gcv.com/invite/${Math.random().toString(36).substr(2, 9)}`
        };
        
        setInvites(prev => [newInvite, ...prev]);
        
        await dispatch(trackEvent({
          eventCategory: 'pos_invite',
          event: 'invite_sent',
          values: {
            retailerEmail: pendingInviteData.retailerEmail,
            retailerName: pendingInviteData.retailerName,
            userRole: userRole,
            brandName: brandName
          }
        }));
        
        setShowSuccessMessage(true);
        setTimeout(() => {
          setShowSuccessMessage(false);
          setActiveTab('tracking');
          handleLoadInvitationTracking();
        }, 4000);
      } else {
        throw new Error('Error sending invite');
      }
    } catch (error) {
      console.error('Error sending invite:', error);
    } finally {
      setIsLoading(false);
      setPendingInviteData(null);
    }
  };

  const handleCancelInvite = () => {
    setShowConfirmDialog(false);
    setPendingInviteData(null);
  };

  const handleLoadStates = async () => {
    try {
      const result = await dispatch(getStates())
      if (result.payload && (result.payload as any).states) {
        setStates((result.payload as any).states)
        if (defaultStateCode && !selectedStateCode) {
          setSelectedStateCode(defaultStateCode);
        }
      }
    } catch (error) {
      console.error('Error loading states:', error)
    }
  };

  const handleLoadRetailers = async () => {
    if (!selectedStateCode) return;
    try {
      const result = await dispatch(getRetailers(selectedStateCode))
      if (result.payload && (result.payload as any).retailers) {
        setRetailers((result.payload as any).retailers)
      }
    } catch (error) {
      console.error('Error in request:', error)
    }
  };

  const handleStateChange = (stateCode: string) => {
    setSelectedStateCode(stateCode);
    setRetailers([]);
  };

  const handleLoadApiSynced = async () => {
    try {
      const result = await dispatch(getApiSynced())
      if (result.payload && (result.payload as any).apiSynced) {
        setApiSynced((result.payload as any).apiSynced)
      }
    } catch (error) {
      console.error('Error in API Synced request:', error)
    }
  };

  const handleLoadInvitationTracking = async () => {
    try {
      const result = await dispatch(getInvitationTracking())
      if (result.payload && (result.payload as any).invitationTracking) {
        setInvitationTracking((result.payload as any).invitationTracking)
      }
    } catch (error) {
      console.error('Error in Invitation Tracking request:', error)
    }
  };

  const handleTabChange = (tab: 'invite' | 'tracking') => {
    setActiveTab(tab);
    dispatch(trackEvent({
      eventCategory: 'pos_invite',
      event: 'tab_changed',
      values: { tab, userRole }
    }));
  };

  const getStatusIcon = (status: InviteData['status']) => {
    const normalizedStatus = status?.toLowerCase();
    
    switch (normalizedStatus) {
      case 'sent':
        return <FiMail size={16} />;
      case 'opened':
        return <FiClock size={16} />;
      case 'completed':
        return <FiCheckCircle size={16} />;
      case 'expired':
        return <FiXCircle size={16} />;
      default:
        return <FiMail size={16} />;
    }
  };

  const getStatusColor = (status: InviteData['status']) => {
    const normalizedStatus = status?.toLowerCase();
    
    switch (normalizedStatus) {
      case 'sent':
        return '#FFA500';
      case 'opened':
        return '#0066CC';
      case 'completed':
        return '#00CC00';
      case 'expired':
        return '#CC0000';
      default:
        return '#666666';
    }
  };

  const getStatusText = (status: InviteData['status']) => {
    const normalizedStatus = status?.toLowerCase();
    
    switch (normalizedStatus) {
      case 'sent':
        return 'Sent';
      case 'opened':
        return 'Opened';
      case 'completed':
        return 'Completed';
      case 'expired':
        return 'Expired';
      default:
        return status || 'Unknown';
    }
  };

  return (
    <>
      <div className="pos-invite-manager-content">

        <div className="pos-invite-tabs">
          <Button
            color={activeTab === 'invite' ? 'brand' : undefined}
            onClick={() => handleTabChange('invite')}
            className={`tab-button ${activeTab === 'invite' ? 'primary' : 'secondary'}`}
          >
            <FiMail size={16} />
            <Text textStyle="button">New Invitation</Text>
          </Button>
          <Button
            color={activeTab === 'tracking' ? 'brand' : undefined}
            onClick={() => handleTabChange('tracking')}
            className={`tab-button ${activeTab === 'tracking' ? 'primary' : 'secondary'}`}
          >
            <FiUsers size={16} />
            <Text textStyle="button">{`Tracking (${invitationTracking.length})`}</Text>
          </Button>
        </div>


        <div className="pos-invite-content">
          {activeTab === 'invite' ? (
            <InviteForm
              onSubmit={handleSendInvite}
              isLoading={isLoading}
              userRole={userRole}
              brandName={brandName}
              retailers={retailers}
              apiSynced={apiSynced}
              states={states}
              selectedStateCode={selectedStateCode}
              onStateChange={handleStateChange}
            />
          ) : (
            <InviteList
              invites={invites}
              getStatusIcon={getStatusIcon}
              getStatusColor={getStatusColor}
              getStatusText={getStatusText}
              invitationTracking={invitationTracking}
            />
          )}
        </div>

      </div>

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title="Confirm Invitation"
        message={`Invite ${pendingInviteData?.retailerName} to connect POS data?`}
        onConfirm={handleConfirmInvite}
        onCancel={handleCancelInvite}
        confirmText="Confirm"
        cancelText="Cancel"
      />

      {showSuccessMessage && (
        <SuccessMessage>
          ✅ Invitation sent successfully!
        </SuccessMessage>
      )}
    </>
  );
};

export default POSInviteManager;
